import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import type { WebConversationDecision, WebConversationDecisions } from '../pages/api/conversation-decisions';

// One stable empty list. Callers memoize on this array and run effects on it (MessagesPanel's
// `approvals` → `setApprovalPollMs`); a fresh `[]` per render re-ran that effect on every keystroke of a
// new chat (query disabled, no data) and tripped React's nested-update limit — the same failure #10519
// fixed for `visible`.
const NO_DECISIONS: WebConversationDecision[] = [];

/**
 * The OPEN questions this conversation raised — so a brief can be ANSWERED where it asks.
 *
 * The rows are the same `WebLedgerDecision` the goal panel already renders through
 * `DecisionAnswerBlock`; only the way they are found differs (by conversation, not by goal). That is the
 * whole point: a second answering mechanism is what produced the bug this closes — a question answered in
 * chat resolved the turn and settled no row, so the next morning asked it again.
 *
 * **An empty list and a failed read are the same thing HERE, deliberately** — and that is the opposite of
 * `useGoalLedger`'s rule, so it is worth saying why. The ledger renders a section that must not claim
 * "nothing happened" when it could not look. This renders nothing at all when there is nothing to answer,
 * so a failed read degrades to the prose the brief has always been. There is no empty state to lie with.
 *
 * A verdict lands as a task write, so the agent-task duplexer refetches — an answered question has to
 * leave the card, or the reader answers it twice.
 */
export function useConversationDecisions(
  conversationId: string | undefined,
  options: {
    /** Also return this thread's CLOSED meeting approvals — only a chat holding an approval row asks. */
    includeClosedMeeting?: boolean;
    /** Also return this thread's CLOSED proposals — only a chat holding a proposals-card row asks. */
    includeClosedProposals?: boolean;
    /** Re-read whenever this changes (the approval thread passes its message count, so a settle made on
     *  WhatsApp or the answer turn that follows it reaches the card without a reload). */
    refreshKey?: unknown;
    /** Re-read on this interval while set — an open meeting approval's verdict can be cast elsewhere with
     *  nothing arriving here (see `meetingApprovalPollDelayMs`). */
    pollMs?: number;
  } = {},
): WebConversationDecision[] {
  const includeClosedMeeting = !!options.includeClosedMeeting;
  const includeClosedProposals = !!options.includeClosedProposals;
  const query = useQuery({
    queryKey: queryKeys.conversationDecisions(conversationId, includeClosedMeeting, includeClosedProposals),
    queryFn: () => fetchConversationDecisions(conversationDecisionsQuery(conversationId as string, { includeClosedMeeting, includeClosedProposals })),
    enabled: !!conversationId,
    refetchInterval: options.pollMs ?? false,
  });

  useAgentTaskDuplexer(() => {
    if (conversationId) void query.refetch();
  });

  // Only a CHANGE within one conversation re-reads. The first key a thread reports arrives with the flag
  // above flipping (or with a new conversation), which is a new query that fetches by itself.
  const lastRefresh = useRef({ conversationId, key: options.refreshKey });
  useEffect(() => {
    const previous = lastRefresh.current;
    lastRefresh.current = { conversationId, key: options.refreshKey };
    if (previous.conversationId !== conversationId || previous.key === undefined || previous.key === options.refreshKey) return;
    if (conversationId) void query.refetch();
  }, [options.refreshKey, conversationId, query]);

  return query.data?.decisions ?? NO_DECISIONS;
}

/** The route's query string. Each closed-rows flag is sent only when set; both may ride together. */
export function conversationDecisionsQuery(
  conversationId: string,
  flags: { includeClosedMeeting?: boolean; includeClosedProposals?: boolean },
): string {
  const params = new URLSearchParams({ conversationId });
  if (flags.includeClosedMeeting) params.set('includeClosedMeeting', '1');
  if (flags.includeClosedProposals) params.set('includeClosedProposals', '1');
  return params.toString();
}

async function fetchConversationDecisions(query: string): Promise<WebConversationDecisions> {
  const res = await apiFetch(`${API_BASE}/api/conversation-decisions?${query}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as Partial<WebConversationDecisions>;
  return { decisions: data.decisions ?? [] };
}
